// Fake-content generators for the Scan-App's DEBUG-only simulate path.
//
// In normal use the Scan-App captures real CSV measurements, real photos,
// and a real OBJ from the browser-side capture flow's on-device 3D
// reconstruction. Behind `VITE_DEBUG=1`, the simulate path fabricates
// bytes that are readable enough that an operator browsing the S3 console
// sees "yep, this is a placeholder, looks intentional" rather than "is
// this corrupt?".
//
// Filenames are FLAT — no slash-separated subfolders — to satisfy the
// presigned-uploads endpoint's filename regex `^(?!\.)[a-zA-Z0-9._-]+$`.
// Side is encoded in the filename (`left_photo1.jpg`, `model_l.obj`,
// etc.), not in a separate path segment. The 10-file layout the simulate
// path emits is just one possible choice — the real capture flow is free
// to upload any number of files with any (regex-compatible) names.

export type Side = "left" | "right";

export interface FakeFile {
  filename: string;
  blob: Blob;
  contentType: string;
}

/**
 * Tiny CSV with an `index,x,y,z` header + N random rows. Bytes ≈ 50–200B.
 * Marked with a `# STUB` line so the file is obviously synthetic in S3.
 */
export function makeMesCsv(side: Side, rows = 10): Blob {
  const lines: string[] = [];
  lines.push(`# STUB ${side}_mes.csv generated by footscan-webapp stub`);
  lines.push("index,x,y,z");
  for (let i = 0; i < rows; i++) {
    const x = (Math.random() * 100).toFixed(3);
    const y = (Math.random() * 100).toFixed(3);
    const z = (Math.random() * 30).toFixed(3);
    lines.push(`${i},${x},${y},${z}`);
  }
  const text = lines.join("\n") + "\n";
  return new Blob([text], { type: "text/csv" });
}

/**
 * 256×256 JPEG with a gradient + readable text overlay. Uses a canvas in
 * the DOM, so this is browser-only. Resolves to a Blob.
 *
 * The visual hint ("STUB photo 1 — left") makes it obvious in any S3
 * preview that the upload came from the simulate path, not a real device.
 */
export async function makePhotoJpeg(side: Side, index: 1 | 2 | 3): Promise<Blob> {
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get 2d canvas context");

  // Gradient background — different hue per index so the three photos look
  // visibly distinct in a thumbnail list.
  const hue = (index * 80 + (side === "left" ? 0 : 40)) % 360;
  const grad = ctx.createLinearGradient(0, 0, size, size);
  grad.addColorStop(0, `hsl(${hue}, 70%, 60%)`);
  grad.addColorStop(1, `hsl(${(hue + 60) % 360}, 70%, 35%)`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);

  // Text overlay.
  ctx.fillStyle = "rgba(0, 0, 0, 0.65)";
  ctx.fillRect(0, size / 2 - 28, size, 56);
  ctx.fillStyle = "#fff";
  ctx.font = "bold 18px sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(`STUB photo ${index}`, size / 2, size / 2 - 10);
  ctx.font = "14px sans-serif";
  ctx.fillText(`${side} foot`, size / 2, size / 2 + 12);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b);
        else reject(new Error("canvas.toBlob returned null"));
      },
      "image/jpeg",
      0.85,
    );
  });
}

/**
 * Minimal-but-valid OBJ — eight vertices + twelve triangular faces forming
 * a unit cube. Plus a `# STUB` comment so the file is obviously synthetic.
 */
export function makeObj(side: Side): Blob {
  const lines = [
    `# STUB model_${side === "left" ? "l" : "r"}.obj generated by footscan-webapp stub`,
    "# Unit cube — not a real foot mesh",
    "o stub_cube",
    "v 0 0 0",
    "v 1 0 0",
    "v 1 1 0",
    "v 0 1 0",
    "v 0 0 1",
    "v 1 0 1",
    "v 1 1 1",
    "v 0 1 1",
    "f 1 2 3",
    "f 1 3 4",
    "f 5 7 6",
    "f 5 8 7",
    "f 1 5 6",
    "f 1 6 2",
    "f 2 6 7",
    "f 2 7 3",
    "f 3 7 8",
    "f 3 8 4",
    "f 4 8 5",
    "f 4 5 1",
  ];
  return new Blob([lines.join("\n") + "\n"], {
    type: "application/octet-stream",
  });
}

/**
 * Build a 10-file payload: 1 CSV + 3 photos + 1 OBJ per foot, all with
 * flat filenames. This is the simulate path's choice — the real capture
 * flow is free to emit whatever artifacts it produces.
 */
export async function buildFullScanPayload(): Promise<FakeFile[]> {
  const out: FakeFile[] = [];
  for (const side of ["left", "right"] as const) {
    out.push({
      filename: `${side}_mes.csv`,
      blob: makeMesCsv(side),
      contentType: "text/csv",
    });
    for (const i of [1, 2, 3] as const) {
      out.push({
        filename: `${side}_photo${i}.jpg`,
        blob: await makePhotoJpeg(side, i),
        contentType: "image/jpeg",
      });
    }
    out.push({
      filename: `model_${side === "left" ? "l" : "r"}.obj`,
      blob: makeObj(side),
      contentType: "application/octet-stream",
    });
  }
  return out;
}

/**
 * Infer content-type from a filename extension. Used by the "attach extra
 * file" path where the user provides arbitrary File objects.
 */
export function inferContentType(filename: string): string {
  const lower = filename.toLowerCase();
  if (lower.endsWith(".csv")) return "text/csv";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".obj")) return "model/obj";
  if (lower.endsWith(".stl")) return "model/stl";
  if (lower.endsWith(".json")) return "application/json";
  if (lower.endsWith(".txt")) return "text/plain";
  return "application/octet-stream";
}
